import type { ConfidenceLevel, Freshness } from '@dcc/shared';

/**
 * All timestamps arrive as UTC and are localised here, once. Relative labels
 * are used for operational facts because "4m ago" answers the operator's real
 * question faster than an absolute time does.
 */
export function relativeTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'unknown';
  const deltaMs = now.getTime() - Date.parse(iso);
  const future = deltaMs < 0;
  const seconds = Math.abs(deltaMs) / 1000;
  const label =
    seconds < 60
      ? `${Math.round(seconds)}s`
      : seconds < 3600
        ? `${Math.round(seconds / 60)}m`
        : seconds < 172800
          ? `${Math.round(seconds / 3600)}h`
          : `${Math.round(seconds / 86400)}d`;
  return future ? `in ${label}` : `${label} ago`;
}

export function absoluteTime(iso: string | null): string {
  if (!iso) return 'unknown';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function clockTime(iso: string | null): string {
  if (!iso) return '--:--';
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function duration(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours)) return 'unknown';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  return minutes === 0 ? `${whole}h` : `${whole}h ${minutes}m`;
}

export function percent(value: number | null, digits = 0): string {
  if (value === null || !Number.isFinite(value)) return 'unknown';
  return `${value.toFixed(digits)}%`;
}

export function freshnessLabel(freshness: Freshness): string {
  if (freshness.observedAt === null) return 'never observed';
  return `${relativeTime(freshness.observedAt)} · ${freshness.source}`;
}

export function confidenceLabel(confidence: ConfidenceLevel, percentValue: number | null): string {
  if (confidence === 'UNKNOWN') return 'confidence unknown';
  return percentValue === null
    ? confidence.toLowerCase()
    : `${confidence.toLowerCase()} · ${percentValue}%`;
}

export function titleCase(value: string): string {
  return value
    .replace(/[_-]/g, ' ')
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}
